/**
 * Procedural cozy-adventure score — original Web Audio synthesis only.
 * No samples or third-party audio assets (copyright-safe).
 *
 * Mood: bright G major · ~112 BPM · soft plucks, bells, pad, light percussion.
 */

var BPM = 112;
var STEPS_PER_BAR = 16;
var BARS_PER_LOOP = 8;
var LOOP_STEPS = STEPS_PER_BAR * BARS_PER_LOOP; // 128 sixteenths
var LOOKAHEAD_MS = 25;
var SCHEDULE_AHEAD = 0.12;
var FADE_SEC = 0.75;
var MUTE_KEY = 'wesam-adventure-music-muted';
var ENABLED_KEY = 'wesam-adventure-music-enabled';
// Minimal silent WAV — upgrades iOS Safari from ambient (mute-switch) to playback.
var SILENT_WAV =
  'data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQIAAAAAAA==';

// G major palette
var N = {
  G2: 98.0,
  A2: 110.0,
  B2: 123.47,
  C3: 130.81,
  D3: 146.83,
  E3: 164.81,
  Fs3: 185.0,
  G3: 196.0,
  A3: 220.0,
  B3: 246.94,
  C4: 261.63,
  D4: 293.66,
  E4: 329.63,
  Fs4: 369.99,
  G4: 392.0,
  A4: 440.0,
  B4: 493.88,
  C5: 523.25,
  D5: 587.33,
  E5: 659.25,
  G5: 783.99,
};

function stepDuration() {
  return 60 / BPM / 4;
}

function readBool(key, fallback) {
  try {
    var raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return raw === 'true';
  } catch (err) {
    return fallback;
  }
}

function writeBool(key, value) {
  try {
    localStorage.setItem(key, String(!!value));
  } catch (err) {
    // ignore
  }
}

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function AdventureMusicEngine() {
  this.ctx = null;
  this.master = null;
  this.musicGain = null;
  this.timer = null;
  this.nextStepTime = 0;
  this.currentStep = 0;
  this.playing = false;
  this.muted = readBool(MUTE_KEY, false);
  this.userPaused = !readBool(ENABLED_KEY, true);
  this.unlocked = false;
  this.listeners = [];
  this._noiseBuffer = null;
  this._silentEl = null;
  this._sessionWired = false;
  this._visibilityWired = false;
}

AdventureMusicEngine.prototype.getState = function () {
  return {
    playing: this.playing && !this.userPaused,
    muted: this.muted,
    unlocked: this.unlocked,
    reducedMotion: prefersReducedMotion(),
  };
};

AdventureMusicEngine.prototype.subscribe = function (fn) {
  var self = this;
  this.listeners.push(fn);
  return function () {
    self.listeners = self.listeners.filter(function (l) {
      return l !== fn;
    });
  };
};

AdventureMusicEngine.prototype.emit = function () {
  var state = this.getState();
  this.listeners.forEach(function (fn) {
    fn(state);
  });
};

AdventureMusicEngine.prototype.ensureContext = function () {
  if (this.ctx) return this.ctx;
  var Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  // Prefer playback session before creating the context (iOS Safari).
  this.promotePlaybackSession();
  this.ctx = new Ctx();
  this.master = this.ctx.createGain();
  this.master.gain.value = 1;
  this.master.connect(this.ctx.destination);
  this.musicGain = this.ctx.createGain();
  this.musicGain.gain.value = 0;
  this.musicGain.connect(this.master);
  this.wireContextLifecycle();
  return this.ctx;
};

/** iOS mute-switch silences ambient Web Audio; force media playback session. */
AdventureMusicEngine.prototype.promotePlaybackSession = function () {
  try {
    if (typeof navigator !== 'undefined' && navigator.audioSession) {
      navigator.audioSession.type = 'playback';
    }
  } catch (err) {
    // ignore unsupported / read-only
  }
};

/**
 * Play a silent HTMLAudioElement during a user gesture.
 * Helps older iOS versions treat this as media playback (not ringer).
 */
AdventureMusicEngine.prototype.primeHtmlAudioUnlock = function () {
  try {
    if (!this._silentEl) {
      this._silentEl = new Audio(SILENT_WAV);
      this._silentEl.setAttribute('playsinline', '');
      this._silentEl.setAttribute('webkit-playsinline', '');
      this._silentEl.loop = true;
      this._silentEl.volume = 0.01;
      this._silentEl.muted = false;
    }
    var playPromise = this._silentEl.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.catch(function () {});
    }
  } catch (err) {
    // ignore
  }
};

/** Silent buffer tick inside the AudioContext — classic iOS unlock. */
AdventureMusicEngine.prototype.primeWebAudioUnlock = function (ctx) {
  try {
    var buffer = ctx.createBuffer(1, 1, ctx.sampleRate || 22050);
    var src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    src.start(0);
  } catch (err) {
    // ignore
  }
};

AdventureMusicEngine.prototype.wireContextLifecycle = function () {
  if (this._sessionWired || !this.ctx) return;
  this._sessionWired = true;
  var self = this;
  this.ctx.addEventListener('statechange', function () {
    if (self.ctx && self.ctx.state === 'running') {
      self.unlocked = true;
    } else if (self.ctx && self.ctx.state === 'suspended' && self.playing) {
      // Keep UI honest if the browser suspends us mid-play.
      self.unlocked = false;
      self.emit();
    }
  });
  if (!this._visibilityWired && typeof document !== 'undefined') {
    this._visibilityWired = true;
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible') return;
      if (self.userPaused || self.muted || prefersReducedMotion()) return;
      if (!self.playing && !self.unlocked) return;
      self.resumeContext().then(function (ok) {
        if (ok && self.playing && !self.userPaused && !self.muted) {
          self.fadeMusicTo(self.effectiveLevel(), 0.35);
        }
      });
    });
  }
};

/**
 * Resume + unlock. Only reports success when the context is actually running.
 * Must be called from a user gesture on mobile.
 */
AdventureMusicEngine.prototype.resumeContext = function () {
  var self = this;
  this.promotePlaybackSession();
  this.primeHtmlAudioUnlock();
  var ctx = this.ensureContext();
  if (!ctx) return Promise.resolve(false);
  this.primeWebAudioUnlock(ctx);

  var finish = function () {
    var running = ctx.state === 'running';
    self.unlocked = running;
    return running;
  };

  if (ctx.state === 'running') {
    return Promise.resolve(finish());
  }

  return ctx
    .resume()
    .then(finish)
    .catch(function () {
      self.unlocked = false;
      return false;
    });
};

AdventureMusicEngine.prototype.getNoise = function () {
  if (this._noiseBuffer) return this._noiseBuffer;
  var ctx = this.ctx;
  var len = ctx.sampleRate * 0.2;
  var buffer = ctx.createBuffer(1, len, ctx.sampleRate);
  var data = buffer.getChannelData(0);
  for (var i = 0; i < len; i += 1) {
    data[i] = Math.random() * 2 - 1;
  }
  this._noiseBuffer = buffer;
  return buffer;
};

AdventureMusicEngine.prototype.fadeMusicTo = function (value, seconds) {
  if (!this.musicGain) return;
  var now = this.ctx.currentTime;
  var g = this.musicGain.gain;
  g.cancelScheduledValues(now);
  g.setValueAtTime(Math.max(g.value, 0.0001), now);
  var target = Math.max(value, 0.0001);
  if (value <= 0) {
    g.exponentialRampToValueAtTime(0.0001, now + seconds);
  } else {
    g.exponentialRampToValueAtTime(target, now + seconds);
  }
};

AdventureMusicEngine.prototype.effectiveLevel = function () {
  if (this.muted || this.userPaused || prefersReducedMotion()) return 0;
  return 0.22;
};

/** Soft piano / acoustic pluck */
AdventureMusicEngine.prototype.playPluck = function (freq, when, gain) {
  var ctx = this.ctx;
  var out = this.musicGain;
  var g = (gain || 0.08) * 0.9;
  var osc = ctx.createOscillator();
  var osc2 = ctx.createOscillator();
  var amp = ctx.createGain();
  var filter = ctx.createBiquadFilter();
  osc.type = 'triangle';
  osc2.type = 'sine';
  osc.frequency.setValueAtTime(freq, when);
  osc2.frequency.setValueAtTime(freq * 2, when);
  osc2.detune.setValueAtTime(4, when);
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(2400, when);
  amp.gain.setValueAtTime(0.0001, when);
  amp.gain.exponentialRampToValueAtTime(g, when + 0.012);
  amp.gain.exponentialRampToValueAtTime(g * 0.35, when + 0.12);
  amp.gain.exponentialRampToValueAtTime(0.0001, when + 0.55);
  osc.connect(filter);
  osc2.connect(amp);
  filter.connect(amp);
  amp.connect(out);
  osc.start(when);
  osc2.start(when);
  osc.stop(when + 0.6);
  osc2.stop(when + 0.6);
};

/** Warm marimba / glock accent */
AdventureMusicEngine.prototype.playBell = function (freq, when, gain) {
  var ctx = this.ctx;
  var out = this.musicGain;
  var g = gain || 0.05;
  var osc = ctx.createOscillator();
  var mod = ctx.createOscillator();
  var modGain = ctx.createGain();
  var amp = ctx.createGain();
  osc.type = 'sine';
  mod.type = 'sine';
  osc.frequency.setValueAtTime(freq, when);
  mod.frequency.setValueAtTime(freq * 3.2, when);
  modGain.gain.setValueAtTime(freq * 1.4, when);
  modGain.gain.exponentialRampToValueAtTime(0.001, when + 0.25);
  mod.connect(modGain);
  modGain.connect(osc.frequency);
  amp.gain.setValueAtTime(0.0001, when);
  amp.gain.exponentialRampToValueAtTime(g, when + 0.008);
  amp.gain.exponentialRampToValueAtTime(0.0001, when + 0.7);
  osc.connect(amp);
  amp.connect(out);
  mod.start(when);
  osc.start(when);
  mod.stop(when + 0.75);
  osc.stop(when + 0.75);
};

/** Soft pizzicato string */
AdventureMusicEngine.prototype.playPizz = function (freq, when, gain) {
  var ctx = this.ctx;
  var out = this.musicGain;
  var g = gain || 0.045;
  var osc = ctx.createOscillator();
  var amp = ctx.createGain();
  var filter = ctx.createBiquadFilter();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(freq, when);
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(freq * 2.2, when);
  filter.Q.setValueAtTime(4, when);
  amp.gain.setValueAtTime(0.0001, when);
  amp.gain.exponentialRampToValueAtTime(g, when + 0.005);
  amp.gain.exponentialRampToValueAtTime(0.0001, when + 0.22);
  osc.connect(filter);
  filter.connect(amp);
  amp.connect(out);
  osc.start(when);
  osc.stop(when + 0.25);
};

/** Gentle bass */
AdventureMusicEngine.prototype.playBass = function (freq, when, gain) {
  var ctx = this.ctx;
  var out = this.musicGain;
  var g = gain || 0.1;
  var osc = ctx.createOscillator();
  var amp = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, when);
  amp.gain.setValueAtTime(0.0001, when);
  amp.gain.exponentialRampToValueAtTime(g, when + 0.02);
  amp.gain.exponentialRampToValueAtTime(g * 0.5, when + 0.2);
  amp.gain.exponentialRampToValueAtTime(0.0001, when + 0.45);
  osc.connect(amp);
  amp.connect(out);
  osc.start(when);
  osc.stop(when + 0.5);
};

/** Airy pad chord stab / sustain slice */
AdventureMusicEngine.prototype.playPad = function (freqs, when, dur, gain) {
  var ctx = this.ctx;
  var out = this.musicGain;
  var g = (gain || 0.03) / freqs.length;
  freqs.forEach(function (freq, idx) {
    var osc = ctx.createOscillator();
    var amp = ctx.createGain();
    var filter = ctx.createBiquadFilter();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, when);
    osc.detune.setValueAtTime(idx === 1 ? -6 : idx === 2 ? 5 : 0, when);
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1200, when);
    amp.gain.setValueAtTime(0.0001, when);
    amp.gain.linearRampToValueAtTime(g, when + 0.35);
    amp.gain.setValueAtTime(g, when + dur - 0.4);
    amp.gain.linearRampToValueAtTime(0.0001, when + dur);
    osc.connect(filter);
    filter.connect(amp);
    amp.connect(out);
    osc.start(when);
    osc.stop(when + dur + 0.02);
  });
};

AdventureMusicEngine.prototype.playKick = function (when) {
  var ctx = this.ctx;
  var out = this.musicGain;
  var osc = ctx.createOscillator();
  var amp = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(140, when);
  osc.frequency.exponentialRampToValueAtTime(48, when + 0.12);
  amp.gain.setValueAtTime(0.0001, when);
  amp.gain.exponentialRampToValueAtTime(0.07, when + 0.01);
  amp.gain.exponentialRampToValueAtTime(0.0001, when + 0.22);
  osc.connect(amp);
  amp.connect(out);
  osc.start(when);
  osc.stop(when + 0.25);
};

AdventureMusicEngine.prototype.playRim = function (when) {
  var ctx = this.ctx;
  var out = this.musicGain;
  var osc = ctx.createOscillator();
  var amp = ctx.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(780, when);
  amp.gain.setValueAtTime(0.0001, when);
  amp.gain.exponentialRampToValueAtTime(0.035, when + 0.002);
  amp.gain.exponentialRampToValueAtTime(0.0001, when + 0.05);
  osc.connect(amp);
  amp.connect(out);
  osc.start(when);
  osc.stop(when + 0.06);
};

AdventureMusicEngine.prototype.playShaker = function (when) {
  var ctx = this.ctx;
  var out = this.musicGain;
  var src = ctx.createBufferSource();
  var filter = ctx.createBiquadFilter();
  var amp = ctx.createGain();
  src.buffer = this.getNoise();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(6000, when);
  filter.Q.setValueAtTime(0.8, when);
  amp.gain.setValueAtTime(0.0001, when);
  amp.gain.exponentialRampToValueAtTime(0.028, when + 0.005);
  amp.gain.exponentialRampToValueAtTime(0.0001, when + 0.07);
  src.connect(filter);
  filter.connect(amp);
  amp.connect(out);
  src.start(when);
  src.stop(when + 0.08);
};

AdventureMusicEngine.prototype.scheduleStep = function (step, when) {
  var bar = Math.floor(step / STEPS_PER_BAR) % BARS_PER_LOOP;
  var beat = step % STEPS_PER_BAR;
  var loopCycle = Math.floor(step / LOOP_STEPS);

  // Pad beds on bar starts
  if (beat === 0) {
    if (bar % 2 === 0) {
      this.playPad([N.G3, N.B3, N.D4], when, stepDuration() * 16, 0.034);
    } else if (bar === 3 || bar === 7) {
      this.playPad([N.C3, N.E3, N.G3], when, stepDuration() * 16, 0.032);
    } else {
      this.playPad([N.D3, N.Fs3, N.A3], when, stepDuration() * 16, 0.03);
    }
  }

  // Bass: optimistic root motion
  if (beat === 0 || beat === 8) {
    var bassMap = [N.G2, N.G2, N.C3, N.D3, N.G2, N.E3, N.C3, N.D3];
    this.playBass(bassMap[bar], when, 0.11);
  }
  if (beat === 4 || beat === 12) {
    var fifths = [N.D3, N.D3, N.G2, N.A2, N.D3, N.B2, N.G2, N.A2];
    this.playBass(fifths[bar], when, 0.07);
  }

  // Soft kick on 1 & 3
  if (beat === 0 || beat === 8) this.playKick(when);
  // Rim on 2 & 4
  if (beat === 4 || beat === 12) this.playRim(when);
  // Shaker 8ths
  if (beat % 2 === 0) this.playShaker(when);

  // Melody plucks — bright, spacious
  var melodyA = [N.G4, null, N.B4, null, N.D5, null, N.B4, null, N.A4, null, N.G4, null, N.E4, null, N.D4, null];
  var melodyB = [N.D5, null, N.B4, null, N.G4, null, N.A4, null, N.B4, null, N.D5, null, N.E5, null, N.D5, null];
  var melodyC = [N.E5, null, N.D5, null, N.B4, null, N.G4, null, N.A4, null, N.B4, null, N.C5, null, N.D5, null];
  var melodies = [melodyA, melodyB, melodyA, melodyC];
  var mel = melodies[bar % 4][beat];
  if (mel) this.playPluck(mel, when, 0.07);

  // Pizzicato answer on offbeats
  if (beat === 6 || beat === 14) {
    var pizzNotes = [N.D4, N.G4, N.E4, N.A4, N.B3, N.G4, N.C4, N.D4];
    this.playPizz(pizzNotes[bar], when, 0.04);
  }

  // Marimba / bell sparkles
  if (beat === 3 || beat === 11) {
    var bells = [N.D5, N.E5, N.G5, N.D5, N.B4, N.E5, N.G4, N.A4];
    this.playBell(bells[bar], when, 0.04);
  }

  // Flourish every other loop, bars 6–7
  if (loopCycle % 2 === 1 && (bar === 6 || bar === 7)) {
    if (beat === 2 || beat === 5 || beat === 9 || beat === 13) {
      var flourish = [N.G5, N.E5, N.D5, N.B4, N.A4, N.G4, N.E5, N.D5];
      this.playBell(flourish[(bar === 7 ? 4 : 0) + Math.floor(beat / 4)], when, 0.045);
    }
  }
};

AdventureMusicEngine.prototype.scheduler = function () {
  if (!this.playing || this.userPaused) return;
  var ctx = this.ctx;
  var stepDur = stepDuration();
  while (this.nextStepTime < ctx.currentTime + SCHEDULE_AHEAD) {
    this.scheduleStep(this.currentStep, this.nextStepTime);
    this.nextStepTime += stepDur;
    this.currentStep += 1;
  }
};

AdventureMusicEngine.prototype.startTransport = function () {
  var self = this;
  if (this.timer) {
    this.playing = true;
    this.fadeMusicTo(this.effectiveLevel(), FADE_SEC);
    this.emit();
    return;
  }
  this.playing = true;
  this.nextStepTime = this.ctx.currentTime + 0.05;
  this.scheduler();
  this.timer = setInterval(function () {
    self.scheduler();
  }, LOOKAHEAD_MS);
  this.fadeMusicTo(this.effectiveLevel(), FADE_SEC);
  this.emit();
};

AdventureMusicEngine.prototype.stopTransport = function (fade) {
  this.playing = false;
  if (this.timer) {
    clearInterval(this.timer);
    this.timer = null;
  }
  this.fadeMusicTo(0, fade != null ? fade : FADE_SEC);
  this.emit();
};

AdventureMusicEngine.prototype.unlockFromGesture = function () {
  if (prefersReducedMotion()) {
    this.emit();
    return;
  }
  var self = this;
  // Prefer playing unless the user explicitly paused.
  if (this.userPaused) {
    this.emit();
    return;
  }
  writeBool(ENABLED_KEY, true);
  this.resumeContext().then(function (ok) {
    if (!ok || self.userPaused) {
      self.emit();
      return;
    }
    if (!self.muted) {
      self.startTransport();
    } else {
      self.emit();
    }
  });
};

/** Start on first page interaction (browsers block silent autoplay with sound). */
AdventureMusicEngine.prototype.armAutoStart = function () {
  if (this._autoArmed || prefersReducedMotion()) return;
  this._autoArmed = true;
  var self = this;
  var onFirstGesture = function () {
    document.removeEventListener('pointerdown', onFirstGesture, true);
    document.removeEventListener('keydown', onFirstGesture, true);
    document.removeEventListener('touchstart', onFirstGesture, true);
    document.removeEventListener('touchend', onFirstGesture, true);
    self.unlockFromGesture();
  };
  document.addEventListener('pointerdown', onFirstGesture, true);
  document.addEventListener('keydown', onFirstGesture, true);
  // touchend is more reliable than touchstart for iOS audio unlock.
  document.addEventListener('touchstart', onFirstGesture, true);
  document.addEventListener('touchend', onFirstGesture, true);
  // Do NOT create/resume AudioContext here — that falsely "unlocks" on mobile
  // while the context stays suspended, so UI shows playing with no sound.
};

AdventureMusicEngine.prototype.play = function () {
  this.userPaused = false;
  writeBool(ENABLED_KEY, true);
  if (prefersReducedMotion()) {
    this.emit();
    return;
  }
  var self = this;
  this.resumeContext().then(function (ok) {
    if (!ok || self.userPaused) {
      self.emit();
      return;
    }
    if (self.muted) {
      self.emit();
      return;
    }
    self.startTransport();
  });
};

AdventureMusicEngine.prototype.pause = function () {
  this.userPaused = true;
  writeBool(ENABLED_KEY, false);
  this.stopTransport(FADE_SEC);
  if (this._silentEl) {
    try {
      this._silentEl.pause();
    } catch (err) {
      // ignore
    }
  }
};

AdventureMusicEngine.prototype.togglePlay = function () {
  if (this.playing && !this.userPaused) this.pause();
  else this.play();
};

AdventureMusicEngine.prototype.setMuted = function (muted) {
  this.muted = !!muted;
  writeBool(MUTE_KEY, this.muted);
  var self = this;
  if (!this.muted && this.playing && !this.userPaused) {
    this.resumeContext().then(function (ok) {
      if (ok) self.fadeMusicTo(self.effectiveLevel(), 0.5);
      self.emit();
    });
    return;
  }
  if (this.playing && !this.userPaused) {
    this.fadeMusicTo(this.effectiveLevel(), 0.5);
  }
  this.emit();
};

AdventureMusicEngine.prototype.toggleMute = function () {
  this.setMuted(!this.muted);
};

AdventureMusicEngine.prototype.retain = function () {
  this._retainCount = (this._retainCount || 0) + 1;
  if (this._releaseTimer) {
    clearTimeout(this._releaseTimer);
    this._releaseTimer = null;
  }
};

AdventureMusicEngine.prototype.release = function () {
  var self = this;
  this._retainCount = Math.max(0, (this._retainCount || 0) - 1);
  if (this._retainCount > 0) return;
  if (this._releaseTimer) clearTimeout(this._releaseTimer);
  // Grace so route changes between bird pages don't cut the score.
  this._releaseTimer = setTimeout(function () {
    self._releaseTimer = null;
    if (!self._retainCount && self.playing) self.stopTransport(0.6);
  }, 500);
};

var sharedEngine = null;

export function getAdventureMusicEngine() {
  if (!sharedEngine) sharedEngine = new AdventureMusicEngine();
  return sharedEngine;
}
